import {
  Component,
  computed,
  inject,
  Input,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconButton } from '@angular/material/button';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatInput, MatLabel } from '@angular/material/input';
import { MatPaginator } from '@angular/material/paginator';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource,
} from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, filter, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { User, UserDetails } from '../../shared/models/user/user.model';
import { UserDialogComponent } from '../user-dialog/user-dialog.component';
import { UsersDragAndDropComponent } from '../users-drag-and-drop/users-drag-and-drop.component';

@Component({
  selector: 'dnv-users-table',
  standalone: true,
  templateUrl: './users-table.component.html',
  imports: [
    MatTable,
    MatColumnDef,
    MatHeaderCell,
    MatCell,
    MatHeaderRow,
    MatRow,
    MatCellDef,
    MatHeaderCellDef,
    MatHeaderRowDef,
    MatRowDef,
    TranslatePipe,
    MatIcon,
    MatIconButton,
    MatFormField,
    MatLabel,
    MatInput,
    MatTooltip,
    UsersDragAndDropComponent,
    MatPaginator,
  ],
})
export class UsersTableComponent {
  displayedColumns: string[] = [
    'badgeNumber',
    'lastName',
    'firstName',
    'email',
    'level',
    'role',
    'title',
    'enabled',
    'actions',
  ];

  private readonly dialog = inject(MatDialog);
  private readonly translateService = inject(TranslateService);

  public tableData = input<User[] | null>([]);
  public readonly new = output<UserDetails>();
  public readonly edit = output<User>();
  public readonly import = output<User>();
  public readonly delete = output<User>();
  public readonly deleteAllUsers = output();
  @Input() isDataImportVisible = signal<boolean>(false);
  @Input() isDataImportCompleted = signal<boolean>(false);
  @Input() isDataImportValid = signal<boolean>(false);

  paginator = viewChild(MatPaginator);
  dataSource = computed(() => {
    const data = new MatTableDataSource(this.filteredUsers() ?? []);
    data.paginator = this.paginator() ?? null;
    return data;
  });

  protected readonly isFilterVisible = signal(false);
  protected filterInput = new Subject<string>();
  protected filterValue = toSignal(
    this.filterInput.pipe(debounceTime(150), distinctUntilChanged()),
  );

  protected filteredUsers = this.createFilter();

  public openModifyDialog(user: User) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.ariaLabel = this.translateService.instant(
      'ADMIN_PAGE.USERS.TABLE.DIALOG.MODIFY.TITLE',
    );
    dialogConfig.ariaDescribedBy = this.translateService.instant(
      'ADMIN_PAGE.USERS.TABLE.DIALOG.MODIFY.SUBTITLE',
    );
    dialogConfig.data = user.details;

    const dialogRef = this.dialog.open(UserDialogComponent, dialogConfig);

    dialogRef
      .afterClosed()
      .pipe(
        filter((value) => !!value),
        tap((value) => {
          user.details = value as UserDetails;
          this.edit.emit(user);
        }),
      )
      .subscribe();
  }

  public openAddDialog() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.ariaLabel = this.translateService.instant(
      'ADMIN_PAGE.USERS.TABLE.DIALOG.ADD.TITLE',
    );
    dialogConfig.ariaDescribedBy = this.translateService.instant(
      'ADMIN_PAGE.USERS.TABLE.DIALOG.ADD.SUBTITLE',
    );

    const dialogRef = this.dialog.open(UserDialogComponent, dialogConfig);

    dialogRef
      .afterClosed()
      .pipe(
        filter((value) => !!value),
        tap((value) => {
          this.new.emit(value);
        }),
      )
      .subscribe();
  }

  toggleDragAndDropVisibility() {
    this.isDataImportVisible.set(!this.isDataImportVisible());
  }

  toggleFilterVisibility() {
    this.isFilterVisible.set(!this.isFilterVisible());
    if (!this.isFilterVisible()) {
      this.clearFilter();
    }
  }

  private createFilter() {
    return computed(() => {
      const users = this.tableData();
      const filterText = this.filterValue();

      if (!users || users.length === 0 || !filterText) {
        return users?.sort((a, b) => {
          if (a.id < b.id) {
            return -1;
          } else if (a.id > b.id) {
            return 1;
          } else {
            return 0;
          }
        });
      }

      const lowerCaseFilter = filterText.toLowerCase();

      return users.filter((user) => {
        const details = user.details;
        return (
          String(details?.badgeNumber || '')
            .toLowerCase()
            .includes(lowerCaseFilter) ||
          details?.lastName?.toLowerCase().includes(lowerCaseFilter) ||
          details?.firstName?.toLowerCase().includes(lowerCaseFilter) ||
          details?.email?.toLowerCase().includes(lowerCaseFilter) ||
          String(details?.level || '')
            .toLowerCase()
            .includes(lowerCaseFilter) ||
          details?.role?.toLowerCase().includes(lowerCaseFilter) ||
          details?.title?.toLowerCase().includes(lowerCaseFilter) ||
          String(details?.enabled || '')
            .toLowerCase()
            .includes(lowerCaseFilter)
        );
      });
    });
  }

  applyFilter(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    this.filterInput.next(value);
  }

  clearFilter() {
    this.filterInput.next('');
  }
}
